/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages, useIntl } from 'react-intl';
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import keyBy from 'lodash/keyBy';
import hash from 'object-hash';
import classnames from 'classnames';
import { SkeletonText } from 'carbon-addons-iot-react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { Warning24, User24 } from '@carbon/icons-react';

import { BATCH_ACTIONS } from '../../constants/constants';
import {
  determineDefaultAlertTableColumns,
  determineAvailableColumnsForAlertsTable,
  determineDefaultViewMetadata,
  determineAlertTableColumns,
  mapAlertDataToTableRows,
} from '../../utils/alertUtils';
import {
  AlertsType,
  OnCreateWorkorderType,
  UpdateAlertsType,
  AlertType,
} from '../../types/AlertsTypes';
import CommonTableWithViews, {
  CommonTableWithViewsType,
} from '../CommonTableWithViews/CommonTableWithViews';
import TableDateSelector from '../CommonTable/TableDateSelector/TableDateSelector';

import { CommonTable } from 'Common';
import { SortDirection } from 'CommonTypes';
import { EntityTypesType } from 'EntitiesTypes';
import { TableViewType, ViewColumnType, PALTableType } from 'TableTypes';
import { DomainsType } from 'KPITypes';

interface AlertsTableType {
  data?: AlertsType;
  /** Dashboard data is only passed in the case of rendering this in the dashboard */
  dashboardData?: AlertsType;
  /** available domains needed to markup the table's severity column values */
  severityDomains?: DomainsType;
  /** available status values */
  statusDomains?: DomainsType;
  availableOwners?: string[];
  className?: string;
  /** should we show the alert name column? */
  hasAlertName?: boolean;
  /** We need the full list of entityTypes so we can fill in the name from the id */
  entityTypes?: EntityTypesType;
  /** should we a large no data message or small */
  hasLargeNoData?: boolean;
  /** Callback value to update alerts */
  onUpdateAlerts?: UpdateAlertsType;
  /** Date change callback; sends an object with {startDate: unixEpoch; endDate: unixEpoch} */
  onDateChange?: (date: { startDate: number; endDate: number }) => any;
  /** instead of rendering our own date picker; should we use custom content? */
  customToolbarContent?: React.ReactNode;
  /** optional custom title */
  title?: React.ReactNode;
  /** optional description tooltip */
  tooltip?: JSX.Element;
  size?: { height?: number; width?: number };
  isExpanded?: boolean;
  /** is the table in preview mode? Makes everything non-editable */
  isPreview?: boolean;
  /** list of available maximo services */
  maximoServices?: Record<string, unknown>[];
  /** Callback with the workorder details to create; which service to use; passes the full alert object that was clicked */
  onCreateWorkorder?: OnCreateWorkorderType;
  /** Callback to bulk change status called with an array of selected alert ids */
  onBulkChangeStatus?: (alertIds: string[]) => void;
  /** Callback to bulk change owner called with an array of selected alert ids */
  onBulkChangeOwner?: (alertIds: string[]) => void;
  /** The number of rows to display on each page */
  numberOfRowsPerPage?: number;
  /** Whether or not to render the table rows as their "short" variant */
  hasShortRows?: boolean;
  /** The current visible state and order of each column, this is only set in the DashboardEditor case */
  dashboardEditorColumns?: ViewColumnType[];
  /** Id that can be used for testing */
  testId?: string;
  userId?: string;
  selectedView?: TableViewType;
  setSelectedView?: React.Dispatch<React.SetStateAction<TableViewType>>;
  dataError?: any;
  /** uses CommonTableWithViews vs CommonTable */
  hasTableViewManagement?: boolean;
  /** is the alert data fetching */
  isFetchingData?: boolean;
  /** is additional data needed */
  isFetchingAdditionalData?: boolean;
  selectedTimeRange?: any;
  setSelectedTimeRange?: any;
  timeRangeOptions?: any;
  isHierarchyDashboard?: boolean;
  accessControls: Array<string>;
}

const messages = defineMessages({
  customColumnsText: 'Dimension columns',
  alerts: 'Alerts',
  events: 'events',
  noDataForRange: 'There is no data for this range',
  noData: 'No data',
  changeStatus: 'Change status',
  changeOwner: 'Assign to...',
  view: 'View',
  edited: 'Edited',
  viewAll: 'View all',
  saveAsNewView: 'Save as new view',
  saveChanges: 'Save changes',
  manageViews: 'Manage views',
  ariaLabel: 'Select view',
  tableViewMenu: 'Table view menu',
  customColumnText: 'Dimension columns',
});

const defaultProps = {
  className: null,
  data: [], // this is data from the container's local state
  dashboardData: null, // this is data from the dashboard
  availableOwners: [],
  severityDomains: [],
  statusDomains: [],
  title: null,
  hasAlertName: true,
  hasLargeNoData: false,
  hasShortRows: false,
  entityTypes: [],
  customToolbarContent: null,
  numberOfRowsPerPage: 10,
  dashboardEditorColumns: [],
  onUpdateAlerts: null,
  onDateChange: null,
  size: {},
  tooltip: null,
  isExpanded: false,
  isPreview: false,
  dataError: null,
  maximoServices: [],
  onBulkChangeOwner: null,
  onBulkChangeStatus: null,
  onCreateWorkorder: null,
  testId: 'alert-table-content',
  userId: undefined,
  selectedView: null,
  setSelectedView: null,
  hasTableViewManagement: false,
  isFetchingData: false,
  isFetchingAdditionalData: false,
  selectedTimeRange: null,
  setSelectedTimeRange: null,
  timeRangeOptions: null,
  isHierarchyDashboard: false,
};
/**
 * Used when the entity type object is fetched from different API's.
 * The v1 api returns entity types with dataItem where as V2 does not.
 * So these manupulations are needed when we have alert table with view enabled.
 * @param entityType
 * @returns
 */
const entityTypeIterater = (entityType): string =>
  entityType.hasOwnProperty('resourceId') ? entityType.resourceId : entityType.entityTypeId;

/**
 * Responsible for:
 * rendering the table
 * creating sample data for editor preview
 * setting up handlers for the table
 * setting up metadata for the table
 */
const AlertsTable = ({
  className,
  data, // this is data from the container's local state
  dashboardData, // this is data from the dashboard
  availableOwners,
  severityDomains,
  statusDomains,
  title,
  hasAlertName,
  hasLargeNoData,
  hasShortRows,
  entityTypes,
  customToolbarContent,
  numberOfRowsPerPage: numberOfRowsPerPageProp,
  dashboardEditorColumns,
  onUpdateAlerts,
  onDateChange,
  size,
  tooltip,
  isExpanded,
  isPreview,
  dataError,
  maximoServices,
  onBulkChangeOwner,
  onBulkChangeStatus,
  onCreateWorkorder,
  testId,
  userId,
  selectedView,
  setSelectedView,
  hasTableViewManagement,
  isFetchingData,
  isFetchingAdditionalData,
  selectedTimeRange,
  setSelectedTimeRange,
  timeRangeOptions,
  isHierarchyDashboard,
  accessControls,
}: AlertsTableType): JSX.Element => {
  const { formatMessage, locale, timeZone } = useIntl();
  const { height } = size;

  const [shouldScroll, setShouldScroll] = useState(!!height || numberOfRowsPerPageProp > 10);

  const entityTypesMap = useMemo(() => keyBy(entityTypes, entityTypeIterater), [entityTypes]);

  const isTableInDashboard = useMemo(
    () => !!(isPreview || dashboardData),
    [isPreview, dashboardData]
  );

  // Figure out what the default behavior of the Alert table columns should be
  const defaultAlertTableColumns = useMemo(
    () =>
      determineDefaultAlertTableColumns({
        availableOwners,
        entityTypesMap,
        formatMessage,
        hasAlertName,
        hasShortRows,
        isPreview,
        isTableInDashboard,
        locale,
        timeZone,
        maximoServices,
        onCreateWorkorder,
        onUpdateAlerts,
        severityDomains,
        statusDomains,
        isHierarchyDashboard,
        userId,
        accessControls,
      }),
    [
      availableOwners,
      entityTypesMap,
      formatMessage,
      hasAlertName,
      hasShortRows,
      isPreview,
      isTableInDashboard,
      locale,
      timeZone,
      maximoServices,
      onCreateWorkorder,
      onUpdateAlerts,
      severityDomains,
      statusDomains,
      isHierarchyDashboard,
      userId,
      accessControls,
    ]
  );

  // Mock data for the editor case
  const sampleData: AlertsType = useMemo(
    () =>
      Array.from(
        { length: 50 },
        (v, i): AlertType =>
          (!isEmpty(dashboardEditorColumns)
            ? (dashboardEditorColumns as { id: string }[])
            : (defaultAlertTableColumns as { id: string }[])
          ).reduce(
            (acc, { id }) => ({
              ...acc,
              alertId: i,
              [id]: id === 'timestamp' ? moment().valueOf() : '--',
            }),
            {
              alertId: undefined,
              entityTypeId: undefined,
              entityId: undefined,
              name: undefined,
              beginTimestamp: undefined,
              status: undefined,
              domainStatus: undefined,
              severity: undefined,
              owner: undefined,
              dimensions: {},
              actions: [],
            }
          )
      ),
    [dashboardEditorColumns, defaultAlertTableColumns]
  );

  // if we're in preview mode use memoized sample data
  const tableData: AlertsType = useMemo(() => {
    if (isPreview) {
      return sampleData;
    }
    return !isNil(dashboardData) ? dashboardData : data;
  }, [data, dashboardData, sampleData, isPreview]);

  const numberOfRowsPerPage = useMemo(
    () => numberOfRowsPerPageProp || (!isNil(height) ? Math.floor((height - 48 * 2) / 48) : 10),
    [height, numberOfRowsPerPageProp]
  );

  /** This keeps track of the columns that should be selectable for the Alert Table */
  const availableColumns = useMemo(
    () =>
      determineAvailableColumnsForAlertsTable({
        defaultAlertTableColumns,
        entityTypes,
      }),
    [entityTypes, defaultAlertTableColumns]
  );

  /** Figure out the actual columns that should show in the alert table, based on the view configuration */
  const configuredAlertTableColumns = useMemo(
    () =>
      determineAlertTableColumns({
        viewColumns: isTableInDashboard
          ? dashboardEditorColumns?.filter(({ isVisible }) => isVisible)
          : selectedView?.columns,
        defaultAlertTableColumns,
        alerts: tableData,
        entityTypesMap,
      }),
    [
      dashboardEditorColumns,
      defaultAlertTableColumns,
      entityTypesMap,
      isTableInDashboard,
      selectedView?.columns,
      tableData,
    ]
  );

  // Need to determine the default view that should be used for the Table if none exists
  const defaultViewMetadata = useMemo(
    () =>
      determineDefaultViewMetadata({
        defaultAlertTableColumns,
      }),
    [defaultAlertTableColumns]
  );

  const formattedTableData = useMemo(
    () => mapAlertDataToTableRows(tableData, formatMessage, entityTypesMap),
    [formatMessage, tableData, entityTypesMap]
  );

  /** Find which column the table is sorted by */
  const sortColumn = dashboardEditorColumns?.find(({ sortDirection }) => sortDirection);

  const isLoading = useMemo(
    () => isFetchingData || isFetchingAdditionalData,
    [isFetchingData, isFetchingAdditionalData]
  );
  const tableView = useMemo(
    () => ({
      table: {
        loadingState: {
          isLoading,
        },
        emptyState:
          dataError || !hasLargeNoData ? (
            <div style={{ textAlign: 'center' }}>{formatMessage(messages.noData)}</div>
          ) : undefined,
        ...(!hasTableViewManagement || !selectedView?.table?.sort // if the table is not in a dashboard, default sort should be done later by the CommonTableWithViews component
          ? {
              sort: {
                columnId: sortColumn?.id || 'timestamp',
                direction: sortColumn?.sortDirection || SortDirection.DESC,
              },
            }
          : {}),
      },
      toolbar: {
        // only add customToolbarContent if there is customToolbarContent to show or the alert table is not in a dashboard
        // meaning it needs to use our custom date picker rather than the card date picker
        ...(!isTableInDashboard || customToolbarContent
          ? {
              customToolbarContent: customToolbarContent || (
                <TableDateSelector
                  key="alerts-table-date-selector"
                  onDateChange={onDateChange}
                  selectedTimeRange={selectedTimeRange}
                  setSelectedTimeRange={setSelectedTimeRange}
                  timeRangeOptions={timeRangeOptions}
                />
              ),
            }
          : {}),
        batchActions: [
          {
            id: BATCH_ACTIONS.CHANGE_STATUS,
            labelText: formatMessage(messages.changeStatus),
            renderIcon: Warning24,
            iconDescription: formatMessage(messages.changeStatus),
          },
          {
            id: BATCH_ACTIONS.CHANGE_OWNER,
            labelText: formatMessage(messages.changeOwner),
            renderIcon: User24,
            iconDescription: formatMessage(messages.changeOwner),
          },
        ],
      },
      pagination: {
        pageSize: numberOfRowsPerPage,
        pageSizes: [numberOfRowsPerPage, 25, 100],
        isItemPerPageHidden: !isNil(dashboardData) && !isExpanded,
      },
    }),
    [
      isLoading,
      dataError,
      hasLargeNoData,
      formatMessage,
      hasTableViewManagement,
      selectedView?.table?.sort,
      sortColumn?.id,
      sortColumn?.sortDirection,
      isTableInDashboard,
      customToolbarContent,
      onDateChange,
      selectedTimeRange,
      setSelectedTimeRange,
      timeRangeOptions,
      numberOfRowsPerPage,
      dashboardData,
      isExpanded,
    ]
  );

  const tableActions = useMemo(
    () => ({
      pagination: {
        onChangePage: ({ pageSize }): void => setShouldScroll(pageSize > 10),
      },
      table: {
        onChangeSort: null,
        onRowClicked: null,
      },
      toolbar: {
        onApplyBatchAction: (actionId, alertIds): void => {
          if (actionId === BATCH_ACTIONS.CHANGE_STATUS) {
            onBulkChangeStatus(alertIds.map(alertId => parseInt(alertId, 10)));
          } else if (actionId === BATCH_ACTIONS.CHANGE_OWNER) {
            onBulkChangeOwner(alertIds.map(alertId => parseInt(alertId, 10)));
          }
        },
      },
    }),
    [onBulkChangeOwner, onBulkChangeStatus]
  );

  const style = useMemo(
    () => ({
      '--table-content-height': `${height / 16 - 3 || 33}rem`, // convert table height to rem and subtract the height of the pagination, defaults to 10 rows high
      '--should-scroll': shouldScroll ? 'auto' : 'visible',
    }),
    [height, shouldScroll]
  );

  const i18n = useMemo(
    () => ({
      emptyButtonLabel: null,
      emptyMessage: formatMessage(messages.noDataForRange),
    }),
    [formatMessage]
  );

  const options = useMemo(
    () => ({
      hasPagination: true,
      hasFilter: !isPreview,
      hasSearch: !isPreview,
      hasRowSelection: !isPreview ? 'multi' : false,
    }),
    [isPreview]
  );

  // if we're in a dashboard use the Table component without view selectors
  const TableComponent: React.ComponentType<CommonTableWithViewsType | PALTableType> = useMemo(
    () => (hasTableViewManagement ? CommonTableWithViews : CommonTable),
    [hasTableViewManagement]
  );

  // We have to wait for both the severityDomains and the statusDomains to exist before we can show the table
  // (this is so the function defined above has valid values)
  return (!isPreview && !isLoading) || isPreview ? (
    <TableComponent
      id={testId}
      data-testid={testId}
      tooltip={tooltip}
      style={style}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(!hasTableViewManagement // Need to regen the table in real time if changing it in the editor, but not if we're using the CommonTableWithViews
        ? {
            key: `entity-events-${hash({
              columns: configuredAlertTableColumns,
              numberOfRowsPerPage,
              hasShortRows,
              sort: tableView.table.sort,
              availableColumns,
            })}`,
          }
        : {
            // these special properties are only valid for CommonTableWithViews
            userId,
            tableId: 'alert',
            selectedView,
            setSelectedView,
            customColumnsText: formatMessage(messages.customColumnsText),
            defaultView: defaultViewMetadata,
            availableColumns,
            // Until this feature moves out of beta hide it
            shouldShowCustomizeColumns:
              window.location?.hostname?.includes('dashboard-dev') ||
              window.location?.hostname?.includes('dashboard-beta') ||
              window.location?.hostname?.includes('localhost'),
          })}
      // if we're not showing the name then we're inside the data item tab and should add a title
      secondaryTitle={title || (!hasAlertName ? formatMessage(messages.alerts) : null)}
      className={classnames(className, 'event-table', {
        'event-table-preview': isPreview,
        'entity-event-table-dashboard': !isNil(dashboardData) && !isExpanded,
        'entity-event-table-dashboard-expanded': !isNil(dashboardData) && isExpanded,
        'entity-event-table-dashboard-short-row': hasShortRows,
      })}
      columns={configuredAlertTableColumns}
      data={formattedTableData}
      view={tableView}
      actions={tableActions}
      i18n={i18n}
      options={options}
    />
  ) : (
    // TODO: would like to remove this skeleton text once this feature becomes available:
    // https://github.com/carbon-design-system/carbon-addons-iot-react/issues/2436
    <div className="event-table-skeleton-text">
      <SkeletonText lineCount={3} paragraph />
    </div>
  );
};

AlertsTable.defaultProps = defaultProps;
export default AlertsTable;
